"use client";

import { useMemo, useState } from "react";
import {
  DataTable,
  PillGroup,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  CompactTextArea,
  DraftPanel,
  FieldGroup,
  FormHeader,
} from "@/components/tools/academic-correspondence/kit";
import { Meter, type MeterTone } from "@/components/tools/admissions/meter";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  buildTeachingStatement,
  LENGTH_CHOICES,
  type LengthChoice,
  lengthStatus,
  MAX_TARGET,
  MIN_TARGET,
  sectionTargets,
  TEACHING_BANK,
  type TeachingInput,
  targetWords,
} from "@/lib/tool-kit/admissions/teaching-philosophy";
import { countPlaceholders } from "@/lib/tool-kit/correspondence/compose";
import { parseWholeCount } from "@/lib/tool-kit/essay-templates/text";

/** English strings. A translated content file overrides these through `ui`. */
export const TEACHING_PHILOSOPHY_STATEMENT_GENERATOR_STRINGS = {
  formHeading: "Your teaching",
  tryExample: "Try an example",
  clear: "Clear",
  lengthLabel: "Length target",
  lengthOptions: {
    page1: "One page (about 500 words)",
    page15: "One and a half pages (about 750)",
    page2: "Two pages (about 1,000)",
    custom: "Another length",
  },
  customLabel: "Target (words)",
  customHint: "Between {min} and {max}.",
  lengthHint:
    "Guides to teaching statements ask for one to two single-spaced pages unless the job advertisement says otherwise.",
  contextSection: "What you teach",
  coursesLabel: "Courses or students (optional)",
  coursesPlaceholder:
    "introductory biology and marine ecology to undergraduates",
  coursesHint: "Finishes “I teach …”.",
  beliefSection: "1. Your central belief",
  beliefLabel: "I believe that…",
  beliefPlaceholder: "students learn science by doing the thinking themselves",
  beliefExampleLabel: "A moment that shows it (optional, full sentences)",
  beliefExamplePlaceholder:
    "In a lecture on population growth, I do not show the curve first…",
  methodsSection: "2. Methods in practice",
  methodsLabel: "In practice, I…",
  methodsPlaceholder:
    "begin most class sessions with a short problem students solve in pairs",
  methodsExampleLabel: "A concrete example (optional, full sentences)",
  methodsExamplePlaceholder:
    "In my field course, students design and run their own survey…",
  evidenceSection: "3. Evidence that it works",
  evidenceLabel: "Evidence (full sentences)",
  evidencePlaceholder:
    "Grades, completion, student work, comments, observations — whatever shows the effect.",
  evidenceHint:
    "Use only what is true. Exact numbers beat adjectives; a single honest observation beats a vague claim.",
  assessmentSection: "4. Assessment and feedback",
  assessmentLabel: "I assess learning by…",
  assessmentPlaceholder:
    "combining short weekly quizzes with two larger data reports",
  feedbackLabel: "How you give and use feedback (optional, full sentences)",
  feedbackPlaceholder:
    "I return reports within a week with comments on the reasoning…",
  growthSection: "5. Growth",
  growthLabel: "I continue to develop as a teacher by…",
  growthPlaceholder:
    "observing colleagues’ classes and revising one activity each year",
  goalLabel: "Next, I want to… (optional)",
  goalPlaceholder: "learn more about universal design for learning",
  draftHeading: "Your teaching statement",
  copy: "Copy",
  copyPlain: "Copy plain text",
  copied: "Copied!",
  download: "Download .txt",
  downloadFilename: "teaching-philosophy-statement.txt",
  placeholdersLeft:
    "one:{count} placeholder left to fill — shown in [brackets]|other:{count} placeholders left to fill — shown in [brackets]",
  noPlaceholders: "No placeholders left in the statement.",
  lengthHeading: "Length against your target",
  noTarget: "Choose a target between {min} and {max} words to compare.",
  wordCount: "one:{count} word|other:{count} words",
  totalLine: "{words} written, against a target of {target}.",
  columns: {
    section: "Paragraph",
    written: "Written",
    target: "Target",
    status: "Status",
  },
  sectionNames: {
    belief: "Belief",
    methods: "Methods",
    evidence: "Evidence",
    assessment: "Assessment",
    growth: "Growth",
  },
  statuses: {
    empty: "Not written",
    short: "Short",
    on: "On target",
    long: "Long",
  },
  tip: "A statement is judged on specifics. Check each paragraph for one example only you could have written, in the present tense, with no jargon the committee would have to decode.",
  example: {
    courses: "introductory biology and marine ecology to undergraduates",
    belief:
      "students learn science by doing the thinking themselves, so my job is to design problems worth thinking about",
    beliefExample:
      "In a lecture on population growth, for example, I do not show the curve first. I give pairs of students a table of fish counts and ask them to predict next year’s numbers, then we compare their reasoning with the model. Students are rarely right on the first try, and that is the point: the gap between their prediction and the data is what the rest of the lecture explains. I want them to leave class able to say why a model works, not only what it predicts.",
    methods:
      "begin most class sessions with a short problem that students solve in pairs, then use their answers to decide what to explain",
    methodsExample:
      "In my field course, students design and run their own survey of a reef site, analyse the data in R, and present the results to the group as if to a funding panel. They meet the same uncertainty a working scientist does, and they learn why method choices matter. Each group also reviews another group’s plan before fieldwork starts, which teaches them to give and take criticism about method rather than about people.",
    evidence:
      "Across three years, the share of students in my section who earned a B or higher on the data-analysis exam rose from 61% to 78%. In end-of-course comments, students most often said that the pair problems made them say their reasoning out loud. After the survey project, one student asked to join a research lab. I treat these results as encouraging rather than conclusive, since my sections are small and the exam changed slightly in the second year.",
    assessment:
      "combining short weekly quizzes that count for little with two larger data reports that count for more, so mistakes come early and cost little",
    feedback:
      "I return reports within a week with comments on the reasoning rather than only the answer, and I ask each student to revise one of them. The revision is where most of the learning shows. For students who miss the first deadline for a good reason, I offer a short extension without a penalty, because the aim is learning and not compliance.",
    growth:
      "observing colleagues’ classes each term, asking for mid-semester feedback, and revising one activity each year based on what students tell me. This year, for example, students said the pair problems were too long, so I cut each to five minutes and moved the discussion to the start of the next class",
    goal: "learn more about universal design for learning so that every activity works for students with different needs",
  },
  ...TEACHING_BANK,
} as const;

const EMPTY: TeachingInput = {
  courses: "",
  belief: "",
  beliefExample: "",
  methods: "",
  methodsExample: "",
  evidence: "",
  assessment: "",
  feedback: "",
  growth: "",
  goal: "",
};

type TextKey = keyof TeachingInput;

const TONES = {
  empty: "idle",
  short: "near",
  on: "ok",
  long: "over",
} as const satisfies Record<string, MeterTone>;

export default function TeachingPhilosophyStatementGeneratorWidget() {
  const strings = useToolUi(TEACHING_PHILOSOPHY_STATEMENT_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [form, setForm] = useState<TeachingInput>(EMPTY);
  const [choice, setChoice] = useState<LengthChoice>("page1");
  const [customRaw, setCustomRaw] = useState("");
  const text = (key: TextKey, id: string) => ({
    id: `tps-${id}`,
    onChange: (value: string) =>
      setForm((current) => ({ ...current, [key]: value })),
    value: form[key],
  });

  const parts = useMemo(
    () => buildTeachingStatement(form, strings),
    [form, strings]
  );
  const paragraphs = parts.map((part) => part.text);
  const target = targetWords(choice, parseWholeCount(customRaw));
  const targets = target === null ? null : sectionTargets(target);
  const total = parts.reduce((sum, part) => sum + part.words, 0);
  const words = (count: number) =>
    tPlural(strings.wordCount, locale, count, { count: fmt(count) });
  const blanks = countPlaceholders(paragraphs);
  const status =
    blanks > 0
      ? tPlural(strings.placeholdersLeft, locale, blanks, {
          count: fmt(blanks),
        })
      : strings.noPlaceholders;
  const totalStatus = target === null ? "empty" : lengthStatus(total, target);

  return (
    <>
      <section className="space-y-5">
        <FormHeader
          clearLabel={strings.clear}
          exampleLabel={strings.tryExample}
          heading={strings.formHeading}
          onClear={() => setForm(EMPTY)}
          onExample={() => {
            setForm({ ...EMPTY, ...strings.example });
            setChoice("page1");
          }}
        />
        <PillGroup
          label={strings.lengthLabel}
          name="tps-length"
          onChange={setChoice}
          options={LENGTH_CHOICES.map((key) => ({
            value: key,
            label: strings.lengthOptions[key],
          }))}
          value={choice}
        />
        {choice === "custom" ? (
          <div className="max-w-xs">
            <TextField
              hint={t(strings.customHint, {
                min: fmt(MIN_TARGET),
                max: fmt(MAX_TARGET),
              })}
              id="tps-custom"
              label={strings.customLabel}
              numeric
              onChange={setCustomRaw}
              value={customRaw}
            />
          </div>
        ) : null}
        <p className={HINT}>{strings.lengthHint}</p>

        <FieldGroup title={strings.contextSection}>
          <TextField
            {...text("courses", "courses")}
            hint={strings.coursesHint}
            label={strings.coursesLabel}
            placeholder={strings.coursesPlaceholder}
          />
        </FieldGroup>
        <FieldGroup title={strings.beliefSection}>
          <CompactTextArea
            {...text("belief", "belief")}
            label={strings.beliefLabel}
            placeholder={strings.beliefPlaceholder}
            rows={2}
          />
          <CompactTextArea
            {...text("beliefExample", "belief-example")}
            label={strings.beliefExampleLabel}
            placeholder={strings.beliefExamplePlaceholder}
            rows={3}
          />
        </FieldGroup>
        <FieldGroup title={strings.methodsSection}>
          <CompactTextArea
            {...text("methods", "methods")}
            label={strings.methodsLabel}
            placeholder={strings.methodsPlaceholder}
            rows={2}
          />
          <CompactTextArea
            {...text("methodsExample", "methods-example")}
            label={strings.methodsExampleLabel}
            placeholder={strings.methodsExamplePlaceholder}
            rows={3}
          />
        </FieldGroup>
        <FieldGroup title={strings.evidenceSection}>
          <CompactTextArea
            {...text("evidence", "evidence")}
            hint={strings.evidenceHint}
            label={strings.evidenceLabel}
            placeholder={strings.evidencePlaceholder}
            rows={4}
          />
        </FieldGroup>
        <FieldGroup title={strings.assessmentSection}>
          <CompactTextArea
            {...text("assessment", "assessment")}
            label={strings.assessmentLabel}
            placeholder={strings.assessmentPlaceholder}
            rows={2}
          />
          <CompactTextArea
            {...text("feedback", "feedback")}
            label={strings.feedbackLabel}
            placeholder={strings.feedbackPlaceholder}
            rows={3}
          />
        </FieldGroup>
        <FieldGroup title={strings.growthSection}>
          <CompactTextArea
            {...text("growth", "growth")}
            label={strings.growthLabel}
            placeholder={strings.growthPlaceholder}
            rows={2}
          />
          <CompactTextArea
            {...text("goal", "goal")}
            label={strings.goalLabel}
            placeholder={strings.goalPlaceholder}
            rows={2}
          />
        </FieldGroup>
      </section>

      <DraftPanel
        filename={strings.downloadFilename}
        heading={strings.draftHeading}
        paragraphs={paragraphs}
        status={status}
        strings={strings}
      >
        <section className="space-y-3">
          <h3 className={WIDGET_H3}>{strings.lengthHeading}</h3>
          {target === null ? (
            <p className={HINT}>
              {t(strings.noTarget, {
                min: fmt(MIN_TARGET),
                max: fmt(MAX_TARGET),
              })}
            </p>
          ) : (
            <>
              <Meter
                caption={t(strings.totalLine, {
                  words: words(total),
                  target: fmt(target),
                })}
                limit={Math.max(target, total)}
                marker={target}
                tone={TONES[totalStatus]}
                value={total}
              />
              <DataTable
                head={[
                  strings.columns.section,
                  strings.columns.written,
                  strings.columns.target,
                  strings.columns.status,
                ]}
                rows={parts.map((part) => [
                  strings.sectionNames[part.section],
                  fmt(part.words),
                  fmt(targets?.[part.section] ?? 0),
                  strings.statuses[
                    lengthStatus(part.words, targets?.[part.section] ?? 0)
                  ],
                ])}
              />
            </>
          )}
          <p className={HINT}>{strings.tip}</p>
        </section>
      </DraftPanel>
    </>
  );
}
